import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle } from "@phosphor-icons/react";

const NAVY = "#0A0F2C";
const GOLD = "#C9A84C";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [form, setForm] = useState({ next: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.next.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (form.next !== form.confirm) { setErr("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.next });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not reset password");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#F0F2F5] py-10 px-4 font-sans">
      <div className="w-full max-w-[420px] rounded-2xl overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.18)] bg-white px-9 py-11"
        data-testid="reset-password-shell">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-[48px] h-[48px] rounded-xl flex items-center justify-center text-[24px] font-semibold"
            style={{ backgroundColor: GOLD, color: NAVY }}>V</div>
          <div className="text-[22px] font-semibold tracking-[1.5px]" style={{ color: NAVY }}>
            VĀ<span style={{ color: GOLD }}>KU</span>
          </div>
        </div>

        {!token ? (
          <div data-testid="reset-password-no-token">
            <p className="text-[13.5px]" style={{ color: "#666" }}>
              This link is missing its reset token. Please request a new one.
            </p>
            <Link to="/forgot-password" data-testid="reset-password-request-new"
              className="mt-4 inline-block text-[13px] font-medium hover:underline" style={{ color: "#185FA5" }}>
              Request a new reset link
            </Link>
          </div>
        ) : done ? (
          <div data-testid="reset-password-success">
            <CheckCircle size={32} weight="fill" className="text-emerald-500 mb-2" />
            <h2 className="text-[20px] font-semibold mb-2" style={{ color: "#0D1117" }}>Password updated</h2>
            <p className="text-[13.5px] mb-4" style={{ color: "#666" }}>You can now sign in with your new password.</p>
            <button data-testid="reset-password-go-login" onClick={() => nav("/login")}
              className="w-full h-[42px] rounded-lg text-[14px] font-semibold text-white" style={{ backgroundColor: NAVY }}>
              Go to sign in
            </button>
          </div>
        ) : (
          <>
            <h2 className="text-[20px] font-semibold mb-1" style={{ color: "#0D1117" }} data-testid="reset-password-title">
              Set a new password
            </h2>
            <p className="text-[13.5px] mb-6" style={{ color: "#666" }}>Choose a strong new password for your account.</p>
            <form onSubmit={submit} className="space-y-[15px]" data-testid="reset-password-form">
              <div>
                <label className="block text-[12.5px] font-medium mb-1.5" style={{ color: "#444" }}>New password</label>
                <input type="password" required data-testid="reset-password-new"
                  value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })}
                  className="w-full h-10 rounded-lg px-3.5 text-[13.5px] outline-none"
                  style={{ border: "1px solid #DDE1E9", backgroundColor: "#FAFAFA", color: "#0D1117" }} />
              </div>
              <div>
                <label className="block text-[12.5px] font-medium mb-1.5" style={{ color: "#444" }}>Confirm new password</label>
                <input type="password" required data-testid="reset-password-confirm"
                  value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  className="w-full h-10 rounded-lg px-3.5 text-[13.5px] outline-none"
                  style={{ border: "1px solid #DDE1E9", backgroundColor: "#FAFAFA", color: "#0D1117" }} />
              </div>
              {err && <div className="text-[12px] font-semibold text-[#DC2626]" data-testid="reset-password-error">{err}</div>}
              <button type="submit" disabled={loading} data-testid="reset-password-submit"
                className="w-full h-[42px] rounded-lg text-[14px] font-semibold text-white disabled:opacity-60"
                style={{ backgroundColor: NAVY }}>
                {loading ? "Updating…" : "Update password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
